// src/Pages/Home/TrendFashion/PageRenderer.tsx
import React from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useGetStorePageQuery, StoreSection } from "../../../redux/services/store/storePagesApi";

import BannerTriptychFashionSection from "./sections/BannerTriptychFashionSection";
import PremiumCategoriesFashionSection from "./sections/PremiumCategoriesFashionSection";
import TopPicksSliderSection from "./sections/TopPicksSliderSection";
import DealOfWeekFashionSection from "./sections/DealOfWeekFashionSection";
import NewArrivalsFashionSection from "./sections/NewArrivalsFashionSection";
import FashionEditorialPortraitCardsSection from "./sections/FashionEditorialPortraitCardsSection";
import FashionHeroNotebookSliderSection from "./sections/FashionHeroNotebookSliderSection";
import InstagramGallerySection from "./sections/InstagramGallerySection";

type Props = {
  pageSlug: string;
};

const renderSection = (section: StoreSection) => {
  switch (section.section_type) {
    case "banner_triptych_fashion":
      return <BannerTriptychFashionSection section={section} />;

    case "premium_categories_fashion":
      return <PremiumCategoriesFashionSection section={section} />;

    case "top_picks_slider":
      return <TopPicksSliderSection section={section} />;

    case "todays_trend_fashion":
      return <TopPicksSliderSection section={section} />;

    case "fashion_editorial_portrait_cards":
      return <FashionEditorialPortraitCardsSection section={section as any} />;

    case "fashion_hero_notebook_slider":
      return <FashionHeroNotebookSliderSection section={section as any} />;

    case "instagram_gallery":
      return <InstagramGallerySection section={section as any} />;

    case "deal_of_week_fashion":
      return <DealOfWeekFashionSection section={section} />;

    case "new_arrivals_fashion":
      return <NewArrivalsFashionSection section={section} />;

    default:
      return null;
  }
};

const skeletonBaseStyle: React.CSSProperties = {
  width: "100%",
  background:
    "linear-gradient(90deg, rgba(240,240,240,0.95) 25%, rgba(248,248,248,1) 37%, rgba(240,240,240,0.95) 63%)",
  backgroundSize: "400% 100%",
  animation: "pageRendererSkeletonShimmer 1.4s ease infinite",
};

const BannerTriptychSkeleton: React.FC = () => {
  return (
    <section className="trend-fashion-home">
      <div className="container-fluid container-custom">
        <Row className="g-0">
          <Col lg={4}>
            <Card className="home-widgets card-height-100 border-0 rounded-end-0 overflow-hidden">
              <Card.Body className="p-0">
                <div
                  style={{
                    ...skeletonBaseStyle,
                    minHeight: 520,
                    height: "100%",
                  }}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="card-height-100 border-0 rounded-0 bg-light overflow-hidden">
              <Card.Body className="px-4 px-lg-5 text-center d-flex align-items-center justify-content-center">
                <div style={{ width: "100%", maxWidth: 420 }}>
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 18,
                      width: "52%",
                      borderRadius: 4,
                      margin: "0 auto 12px",
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 34,
                      width: "86%",
                      borderRadius: 4,
                      margin: "0 auto 10px",
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 34,
                      width: "68%",
                      borderRadius: 4,
                      margin: "0 auto 18px",
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 16,
                      width: "72%",
                      borderRadius: 4,
                      margin: "0 auto 8px",
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 16,
                      width: "58%",
                      borderRadius: 4,
                      margin: "0 auto 28px",
                    }}
                  />
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 44,
                      width: 150,
                      borderRadius: 0,
                      margin: "0 auto",
                    }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="home-widgets card-height-100 border-0 rounded-start-0 overflow-hidden">
              <Card.Body className="p-0">
                <div
                  style={{
                    ...skeletonBaseStyle,
                    minHeight: 520,
                    height: "100%",
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </section>
  );
};

const PremiumCategoriesSkeleton: React.FC = () => {
  return (
    <section>
      <div className="container-fluid container-custom">
        <Row className="justify-content-center">
          <Col lg={7}>
            <div className="section-content text-center mb-5">
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 18,
                  width: 120,
                  borderRadius: 4,
                  margin: "0 auto 16px",
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 38,
                  width: "72%",
                  borderRadius: 4,
                  margin: "0 auto 10px",
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 38,
                  width: "48%",
                  borderRadius: 4,
                  margin: "0 auto",
                }}
              />
            </div>
          </Col>
        </Row>

        <Row className="g-4">
          {[1, 2, 3, 4].map((item) => (
            <Col key={`premium-skeleton-${item}`} xl={3} lg={4} md={6} xs={12}>
              <div
                className="category-widgets-main card border-0 shadow-none bg-light h-100"
                style={{
                  borderRadius: "16px",
                  overflow: "hidden",
                }}
              >
                <div
                  className="effect position-relative overflow-hidden"
                  style={{
                    aspectRatio: "3 / 4",
                    minHeight: "380px",
                  }}
                >
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: "100%",
                      minHeight: "380px",
                    }}
                  />

                  <div
                    className="position-absolute start-0 bottom-0 w-100"
                    style={{ padding: "18px" }}
                  >
                    <div
                      style={{
                        ...skeletonBaseStyle,
                        height: 20,
                        width: "55%",
                        borderRadius: 4,
                        marginBottom: "12px",
                        background:
                          "linear-gradient(90deg, rgba(255,255,255,0.75) 25%, rgba(245,245,245,0.95) 37%, rgba(255,255,255,0.75) 63%)",
                        backgroundSize: "400% 100%",
                      }}
                    />
                    <div
                      style={{
                        ...skeletonBaseStyle,
                        height: 36,
                        width: 110,
                        borderRadius: "999px",
                        background:
                          "linear-gradient(90deg, rgba(255,255,255,0.85) 25%, rgba(245,245,245,1) 37%, rgba(255,255,255,0.85) 63%)",
                        backgroundSize: "400% 100%",
                      }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

const GenericSectionSkeleton: React.FC = () => {
  return (
    <div className="container-fluid container-custom" style={{ paddingBottom: 40 }}>
      <div
        style={{
          ...skeletonBaseStyle,
          height: 26,
          width: 220,
          borderRadius: 4,
          margin: "0 auto 24px",
        }}
      />
      <Row className="g-4">
        {[1, 2, 3, 4].map((item) => (
          <Col key={`generic-skeleton-${item}`} xl={3} lg={4} md={6} xs={12}>
            <div
              style={{
                ...skeletonBaseStyle,
                height: 320,
                borderRadius: 12,
              }}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

const HomePageSkeleton: React.FC = () => {
  return (
    <>
      <style>
        {`
          @keyframes pageRendererSkeletonShimmer {
            0% { background-position: 100% 0; }
            100% { background-position: 0 0; }
          }
        `}
      </style>

      <div
        style={{
          width: "100%",
          minHeight: "70vh",
          background: "#fff",
        }}
      >
        <BannerTriptychSkeleton />
        <PremiumCategoriesSkeleton />
        <GenericSectionSkeleton />
      </div>
    </>
  );
};

const PageRenderer: React.FC<Props> = ({ pageSlug }) => {
  const { data, isLoading, isError } = useGetStorePageQuery({
    pageSlug,
    includeItems: true,
    activeOnly: true,
  });

  if (isLoading) {
    return <HomePageSkeleton />;
  }

  if (isError || !data) {
    return <div style={{ minHeight: "70vh", background: "#fff" }} />;
  }

  const sections: StoreSection[] = (data.sections || [])
    .slice()
    .sort((a: StoreSection, b: StoreSection) => (a.sort_order || 0) - (b.sort_order || 0));

  if (!sections.length) {
    return <div style={{ minHeight: "70vh", background: "#fff" }} />;
  }

  return (
    <div style={{ minHeight: "70vh", background: "#fff" }}>
      {sections.map((s: StoreSection) => (
        <React.Fragment key={s.id}>{renderSection(s)}</React.Fragment>
      ))}
    </div>
  );
};

export default PageRenderer;
